import { Form, Table } from 'react-bootstrap';
import { MdAdd } from 'react-icons/md';
import { Property } from '@blisspointmedia/bpm-types/dist/StreamingNetworks';
import { Checkbox } from '@Components/FormCheck';
import { Button } from '@Components/Button';
import { StreamingNetworkRecord, updateAt } from './streaming-networks-constants';

interface NetworkPropertiesTabProps {
  network: StreamingNetworkRecord;
  editing: boolean;
  onFieldChange: <K extends keyof StreamingNetworkRecord>(
    field: K,
    value: StreamingNetworkRecord[K]
  ) => void;
}

// Sub-properties, once added, can't be removed here (no `deleted` support for this list) — only
// newly-added rows (isNew) get editable Short Code/Name; existing ones are fixed.
export const NetworkPropertiesTab = ({
  network,
  editing,
  onFieldChange
}: NetworkPropertiesTabProps) => {
  const { properties } = network;

  const setProperties = (updated: Property[]) => onFieldChange('properties', updated);

  const addProperty = () =>
    setProperties([
      ...properties,
      {
        propertyShortCode: '',
        propertyName: '',
        propertyNetworkGroup: network.networkGroup,
        buyable: true,
        isNew: true
      }
    ]);

  if (properties.length === 0 && !editing) {
    return (
      <div className='sn-empty'>
        There are currently no sub-properties for this network. By adding one here, you&apos;re
        going to make this network work like Discovery.
      </div>
    );
  }

  return (
    <div className='sn-properties'>
      <Table size='sm' className='sn-properties-table'>
        <thead>
          <tr>
            <th>Short Code</th>
            <th>Name</th>
            <th>Buyable</th>
          </tr>
        </thead>
        <tbody>
          {properties.map((property, index) => (
            <PropertyRow
              key={index}
              property={property}
              editing={editing}
              onChange={updated => setProperties(updateAt(properties, index, updated))}
            />
          ))}
        </tbody>
      </Table>
      {editing && (
        <Button className='sn-add-action' leadingElement={<MdAdd />} onClick={addProperty}>
          Add property
        </Button>
      )}
    </div>
  );
};

interface PropertyRowProps {
  property: Property;
  editing: boolean;
  onChange: (updated: Property) => void;
}

const PropertyRow = ({ property, editing, onChange }: PropertyRowProps) => (
  <tr>
    <td>
      {property.isNew ? (
        <Form.Control
          size='sm'
          value={property.propertyShortCode}
          onChange={event => onChange({ ...property, propertyShortCode: event.target.value })}
          onBlur={event =>
            onChange({ ...property, propertyShortCode: event.target.value.toUpperCase() })
          }
        />
      ) : (
        property.propertyShortCode
      )}
    </td>
    <td>
      {property.isNew ? (
        <Form.Control
          size='sm'
          value={property.propertyName}
          onChange={event => onChange({ ...property, propertyName: event.target.value })}
        />
      ) : (
        property.propertyName
      )}
    </td>
    <td>
      <Checkbox
        checked={property.buyable}
        disabled={!editing}
        onChange={event => onChange({ ...property, buyable: event.target.checked })}
      />
    </td>
  </tr>
);
